/**
 * Gated debug logger.
 *
 * Maturity gap fix (2026-05-14): hot paths in voice/chat were emitting
 * 60+ console.log/warn/error calls per session, leaking SIP traces and
 * encrypted-message metadata to native device logs and the browser
 * console. This wrapper:
 *
 *   • Silences `debug()` and `info()` in production builds entirely.
 *   • Keeps `warn()` / `error()` (those still flow to crash analytics).
 *   • Adds a stable `[scope]` tag so noisy modules can be grep'd quickly
 *     in dev without a full source dive.
 *   • Honours a runtime override:  localStorage.setItem('elefa:debug', '*')
 *     (or a comma-list of scopes) so on-device support sessions can
 *     re-enable verbose tracing without a rebuild.
 *
 * Usage:
 *   const log = createLogger('elefa-call');
 *   log.debug('outgoing INVITE', { dst });
 *   log.error('SIP failure', err);
 */

type Level = "debug" | "info" | "warn" | "error";

const isDev = import.meta.env.DEV;

function runtimeScopes(): Set<string> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage?.getItem("elefa:debug");
    if (!raw) return null;
    if (raw === "*") return new Set(["*"]);
    return new Set(raw.split(",").map((s) => s.trim()).filter(Boolean));
  } catch {
    return null;
  }
}

function shouldEmit(scope: string, level: Level): boolean {
  if (level === "warn" || level === "error") return true;
  if (isDev) return true;
  const scopes = runtimeScopes();
  if (!scopes) return false;
  return scopes.has("*") || scopes.has(scope);
}

export interface ScopedLogger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

export function createLogger(scope: string): ScopedLogger {
  const tag = `[${scope}]`;
  const emit = (level: Level, args: unknown[]) => {
    if (!shouldEmit(scope, level)) return;
    // eslint-disable-next-line no-console
    (console as Record<Level, (...a: unknown[]) => void>)[level](tag, ...args);
  };
  return {
    debug: (...args) => emit("debug", args),
    info: (...args) => emit("info", args),
    warn: (...args) => emit("warn", args),
    error: (...args) => emit("error", args),
  };
}
